/**
 * Melon Auth v2 Composable Function
 *
 * # Brief overview of authentication
 *
 * 1. Player gets a 6-digit code (challenge id) when attempting to log on the server for the first time.
 * 2. Player submits the challenge id via `verifyCode()` to initiate the authentication.
 * 3. Player updates his MC profile skin with the generated file given by the auth server.
 * 4. Player requests for completion of the authentication via `checkResult()`.
 */

import {ref, watch} from '@vue/composition-api'
import axios from 'axios'

import useNow from './now'

const URL_BASE = 'https://api.authv2.langua.moe'

let challengeId = ref('')
let isExpired = ref(process.env.NODE_ENV !== 'development' ? false : false)
let stopWatchExpiration = null
let verificationData = ref(null)

/**
 * Check verification code & fetch verification info
 *
 * @returns {Promise<VueRef<VerificationInfo>, ErrorInfo>}
 *
 * @typedef VerificationInfo
 * @prop uuid      {String}
 * @prop name      {String}
 * @prop expireIn  {Number}
 * @prop url       {String}
 * @prop skinModel {"steve" | "alex"}
 */
export async function verifyCode () {
  try {
    const {data} = await axios(`${URL_BASE}/get/challenge/${challengeId.value}`)
    verificationData.value = data
    if (process.env.NODE_ENV === 'development') {
      verificationData.value.url = data.url.replace(/^https?:\/\/\S+?\//, '/api/')
    }

    if (stopWatchExpiration) {
      isExpired.value = false
      stopWatchExpiration()
      stopWatchExpiration = null
    }

    const expiration = Date.now() + data.expireIn
    let {now} = useNow()
    stopWatchExpiration = watch(now, now => {
      if (now > expiration) {
        isExpired.value = true
        stopWatchExpiration()
        stopWatchExpiration = null
      }
    })

    return verificationData
  } catch ({response}) {
    switch (response?.status) {
      case 404: return Promise.reject({
        error: 'langua.step_pin.error_invalid_code.title',
        errorMessage: 'langua.step_pin.error_invalid_code.content',
      })
      default: return Promise.reject(response?.data ?? createUnexpectedError())
    }
  }
}

/**
 * Check verification result after skin update
 *
 * @returns {Promise<Boolean | ErrorInfo>}
 */
export async function checkResult () {
  try {
    if (process.env.NODE_ENV !== 'development' || true) {
      const {status} = await axios(`${URL_BASE}/verify/${challengeId.value}`)
      switch (status) {
        case 200:
          return true
      }
    } else {
      await new Promise((_, rej) => setTimeout(() => rej({
        response: {
          status: 429,
          data: {
            error: 'TooManyRequestsException',
            errorMessage: 'only the first request will be proceed each minute per uuid',
            extra: '50472',
          },
        },
      }), 2000))
    }
  } catch ({response}) {
    switch (response?.status) {
      case 304: return Promise.reject({
        type: 'warning',
        error: 'langua.step_verification.hint_no_update_detected.title',
        errorMessage: 'langua.step_verification.hint_no_update_detected.content',
      })
      case 429: return Promise.reject({
        type: 'warning',
        error: 'langua.step_verification.hint_too_many_requests.title',
        errorMessage: 'langua.step_verification.hint_too_many_requests.content',
        extra: Number(response.data.extra),
      })
      default: return Promise.reject(response?.data ?? createUnexpectedError())
    }
  }
}

function createUnexpectedError () {
  return {
    error: 'langua.general.unexpected_error.title',
    errorMessage: 'langua.general.unexpected_error.content',
  }
}

export default function useMelonAuth () {
  return {
    challengeId,
    isExpired,
    verificationData,

    verifyCode,
    stopWatchExpiration,
    checkResult,
  }
}

/**
 * @typedef VueRef
 * @prop value {*}
 */

/**
 * @typedef ErrorInfo
 * @prop error        {String}
 * @prop errorMessage {String}
 */
